import React, {
  useCallback,
  useEffect,
  useState,
  KeyboardEvent,
} from 'react';
import {
  PaginationInstance,
  PaginationState,
} from '@tanstack/react-table';
import clsx from 'clsx';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightIcon from '@mui/icons-material/ChevronRightRounded';
import MuiPagination from '@mui/material/Pagination';
import {
  isEmptyString,
  pluralize,
} from '@populateui/utils';
import {
  pageSizeOptions,
} from 'core/constants';
import IconButton from 'core/components/IconButton';
import Select from 'core/components/Select';
import Input from 'pages/Dashboard/components/Input';
import Divider from '@mui/material/Divider';

export type PaginationControlsOptions = {
  showPrevNext?: boolean;
  showPageInput?: boolean;
  showMuiPagination?: boolean;
  showPageSizeSelector?: boolean;
};

type ResolvedPaginationControls = Required<PaginationControlsOptions>;

const defaultControls: ResolvedPaginationControls = {
  showPrevNext: false,
  showPageInput: true,
  showMuiPagination: true,
  showPageSizeSelector: true,
};

export function resolvePaginationControls({
  showPrevNext,
  showPageInput,
  showMuiPagination,
  showPageSizeSelector,
}: PaginationControlsOptions): ResolvedPaginationControls {
  return {
    showPrevNext: showPrevNext ?? defaultControls.showPrevNext,
    showPageInput: showPageInput ?? defaultControls.showPageInput,
    showMuiPagination: showMuiPagination ?? defaultControls.showMuiPagination,
    showPageSizeSelector: showPageSizeSelector ?? defaultControls.showPageSizeSelector,
  };
}

type Parts =
  | 'root'
  | 'pageSizeSelector'
  | 'pageInfo'
  | 'prevNext'
  | 'pageInput'
  | 'pageInputField';

type Props = PaginationState & Partial<PaginationInstance<any>> & {
  totalItems: number;
  className?: string;
  classes?: Partial<Record<Parts, string>>;
  controls?: PaginationControlsOptions;
};

type PageNumberInputProps = {
  pageIndex: number;
  pageCount: number;
  setPageIndex: (index: number) => void;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
};

function parsePageDraft(draft: string): number | null {
  const trimmed = draft.trim();
  const parsed = isEmptyString(trimmed) ? Number.NaN : Number.parseInt(trimmed, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function getValidPageNumber(page: number, pageCount: number): number {
  return Math.min(Math.max(1, page), pageCount);
}

function PageNumberInput({
  pageIndex,
  pageCount,
  setPageIndex,
  disabled = false,
  className,
  inputClassName,
}: PageNumberInputProps) {
  const currentPage = pageIndex + 1;
  const [draft, setDraft] = useState(String(currentPage));

  useEffect(() => {
    setDraft(String(pageIndex + 1));
  }, [pageIndex, pageCount]);

  const updatePageNumber = useCallback(() => {
    const parsed = parsePageDraft(draft);
    const validPageNumber = parsed === null
      ? currentPage
      : getValidPageNumber(parsed, pageCount);

    setDraft(String(validPageNumber));

    const updatedIndex = validPageNumber - 1;
    if (parsed !== null && updatedIndex !== pageIndex) {
      setPageIndex(updatedIndex);
    }
  }, [draft, pageCount, pageIndex, currentPage, setPageIndex]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === 'Enter') {
        updatePageNumber();
      }
    },
    [updatePageNumber],
  );

  const isDisabled = disabled || pageCount <= 1;

  return (
    <div className={clsx('flex flex-row items-center gap-2 text-xs', className)}>
      <span>Go to page</span>
      <Input
        value={draft}
        disabled={isDisabled}
        onChange={(value) => {
          setDraft(value.replace(/\D/g, ''));
        }}
        onBlur={updatePageNumber}
        onKeyDown={handleKeyDown}
        classes={{
          root: 'inline-flex',
          container: clsx('!min-w-0', inputClassName),
          input: 'pagination-input',
        }}
        inputProps={{
          min: 1,
          max: pageCount,
          inputMode: 'numeric',
          'aria-label': 'Page number',
        }}
      />
    </div>
  );
}

type PaginationPrevNextProps = {
  pageIndex: number;
  pageCount: number;
  setPageIndex: (index: number) => void;
  className?: string;
};

function PaginationPrevNext({
  pageIndex,
  pageCount,
  setPageIndex,
  className,
}: PaginationPrevNextProps) {
  const isFirstPage = pageIndex <= 0;
  const isLastPage = pageIndex >= pageCount - 1;
  const isSinglePage = pageCount <= 1;

  return (
    <div className={clsx('flex flex-row items-center gap-0.5', className)}>
      <IconButton
        size="small"
        tooltip="Previous page"
        disabled={isFirstPage || isSinglePage}
        onClick={() => { setPageIndex(pageIndex - 1); }}
        className="!text-gray-500 !p-0.5"
        aria-label="Previous page"
      >
        <ChevronLeftIcon className="!w-5 !h-5" />
      </IconButton>
      <IconButton
        size="small"
        tooltip="Next page"
        disabled={isLastPage || isSinglePage}
        onClick={() => { setPageIndex(pageIndex + 1); }}
        className="!text-gray-500 !p-0.5"
        aria-label="Next page"
      >
        <ChevronRightIcon className="!w-5 !h-5" />
      </IconButton>
    </div>
  );
}

export default function Pagination({
  totalItems,
  setPageIndex,
  setPageSize,
  pageIndex,
  pageSize,
  className,
  classes,
  controls,
}: Props) {
  const {
    showPrevNext,
    showPageInput,
    showMuiPagination,
    showPageSizeSelector,
  } = resolvePaginationControls(controls ?? defaultControls);

  const pageCount = Math.max(1, Math.ceil(totalItems / pageSize));
  const canSetPageIndex = typeof setPageIndex === 'function';
  const canSetPageSize = typeof setPageSize === 'function';

  const itemRangeStart = pageIndex * pageSize + 1;
  const itemRangeEnd = Math.min((pageIndex + 1) * pageSize, totalItems);

  return (
    <div className={clsx('flex flex-row justify-between w-full px-4', className, classes?.root)}>
      {showPageSizeSelector && (
        <div className={clsx('flex flex-row items-center justify-start text-xs', classes?.pageSizeSelector)}>
          Rows per page:
          <Select<number>
            selectClassName="!border-0 relative top-0.5"
            value={pageSize}
            options={pageSizeOptions}
            getValue={(option) => option}
            getLabel={(option) => option.toString()}
            onChange={(option: number | number[]) => {
              if (canSetPageSize) {
                setPageSize(option as number);
              }
            }}
          />
        </div>
      )}

      <div className={clsx('flex flex-row items-center gap-4', classes?.pageInfo)}>
        {showPageInput && canSetPageIndex && (
          <PageNumberInput
            pageIndex={pageIndex}
            pageCount={pageCount}
            setPageIndex={setPageIndex}
            className={classes?.pageInput}
            inputClassName={clsx('!w-12', classes?.pageInputField)}
          />
        )}
        <Divider orientation="vertical" className="!mx-2 !h-6" />
        <div className="!text-xs whitespace-nowrap">
          {itemRangeStart}
          -
          {itemRangeEnd}
          {' of '}
          {pluralize('item', totalItems)}
        </div>
        {showPrevNext && canSetPageIndex && (
          <PaginationPrevNext
            pageIndex={pageIndex}
            pageCount={pageCount}
            setPageIndex={setPageIndex}
            className={classes?.prevNext}
          />
        )}
        {showMuiPagination && canSetPageIndex && (
          <MuiPagination
            size="small"
            page={pageIndex + 1}
            siblingCount={0}
            boundaryCount={2}
            count={pageCount}
            onChange={(event, page: number) => {
              if (page - 1 !== pageIndex) {
                setPageIndex(page - 1);
              }
            }}
          />
        )}
      </div>
    </div>
  );
}
